/* oxlint-disable */
'use client';

import { memo, useCallback, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import emailSpellChecker from '@zootools/email-spell-checker';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
// import Turnstile, { useTurnstile } from 'react-turnstile';

// import Turnstile from 'react-turnstile';
import { z } from 'zod';

import { messages } from '@/features/auth/localization/texts';

import PasswordStrengthMeter from '@/features/validatePassword/PasswordStrengthMeter';
import { userUIStore } from '@/stores/userUIStore';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl, FormField, FormItem, FormLabel,
} from '@/components/ui/form';

import { Input } from '../../../components/ui/input';
import { loginEmailAndPass, registerEmailAndPass } from '../schema';

interface Props {
  action: (values: z.infer<any>) => void,
  disabled: boolean,
  isRegister: boolean,
}

const FormEmailAndPassword = ({ action, disabled, isRegister }:Props) => {
  const { toggleCheckbox } = userUIStore();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  // const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // const apiKey = process.env.NEXT_PUBLIC_TURNSTILE_API_KEY ?? '';
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);

  const formSchema = isRegister ? registerEmailAndPass.extend({
    turnstileToken: z.string().min(1, 'Please complete the Cloudflare challenge'),
    ageVerification: z.boolean().default(false),
  }) : loginEmailAndPass.extend({
    turnstileToken: z.string().min(1, 'Please complete the Cloudflare challenge'),
    ageVerification: z.boolean().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(isRegister ? registerEmailAndPass : loginEmailAndPass),
    defaultValues: {
      email: '', password: '', ageVerification: false, turnstileToken: '',
    },
    mode: 'onChange',
  });

  const validateEmailWithZeroBounce = async (email: string) => {
    try {
      setIsValidatingEmail(true);
      setEmailValidationError(null);
      form.clearErrors('email');

      const response = await fetch('/api/validate-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!data.success) {
        console.warn('Email validation service unavailable:', data.message);
        return true;
      }

      if (data.isDisposable) {
        const reason = 'No se permiten correos electrónicos temporales o desechables';
        setEmailValidationError(reason);
        form.setError('email', { type: 'manual', message: reason });
        return false;
      }

      if (!data.isValid) {
        // You can customize this based on ZeroBounce's response
        const reason = data.result?.status === 'invalid' ? 'Correo electrónico inválido' : data.result?.status === 'catch-all' ? 'Correo electrónico inválido' : data.result?.status === 'unknown' ? 'No se puede verificar el correo electrónico' : 'Correo electrónico inválido';
        setEmailValidationError(reason);
        form.setError('email', { type: 'manual', message: reason });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Email validation error:', error);
      return true;
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (isRegister) {
      if (isValidatingEmail) {
        return;
      }
      
      const isEmailValid = await validateEmailWithZeroBounce(values.email);
      if (!isEmailValid) {
        return;
      }
    }

    action({ ...values });
  };

  const handleEmailBlur = useCallback(() => {
    const email = form.getValues('email');
    const result = emailSpellChecker.run({ email });
    setSuggestion(result?.full ?? null);
    form.trigger('email');
  }, [form]);

  // const handleValidation = async (token: string) => {
  //   try {
  //     const response = await fetch('/api/validate-turnstile', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ token }),
  //     });

  //     const data = await response.json();
  //     if (!data.success) {
  //       // Token invalid, reset the widget
  //       // turnstile.reset();
  //
  //       console.log(data.errors);
  //     }
  //   } catch (error) {
  //     // turnstile.reset();
  //
  //     console.log(error);
  //   }
  // };

  return (
    <div className="flex flex-col zyy gap-2 w-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mx-[17px] mb-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={messages.email}
                      onChange={(value) => { 
                        if (emailValidationError) {
                          setEmailValidationError(null);
                          form.clearErrors('email');
                        }
                        field.onChange(value.target.value.toLowerCase()); 
                      }}
                      value={field.value}
                      name={field.name}
                      type="email"
                      onBlur={() => {
                        field.onBlur();
                        handleEmailBlur();
                      }}
                      className="input-form-text"
                    />
                  </FormControl>
                  {/* <FormMessage /> */}
                  {emailValidationError && (
                    <div className="mt-2 p-2 bg-red-50 rounded-lg text-center">
                      <p className="text-red-600 font-medium">{emailValidationError}</p>
                    </div>
                  )}
                  {suggestion && (
                    <div className="mt-2 p-2 bg-amber-50 rounded-lg text-center">
                      <p className="text-red-600 font-medium">
                        ¿Quieres decir
                        {' '}
                        <button
                          type="button"
                          className="font-bold text-red-700 underline"
                          onClick={() => {
                            form.setValue('email', suggestion);
                            setSuggestion(null);
                            form.clearErrors('email');
                            setEmailValidationError(null);
                          }}
                        >
                          {suggestion}
                        </button>
                        ?
                      </p>
                    </div>
                  )}
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder={messages.password}
                        onChange={field.onChange}
                        value={field.value}
                        name={field.name}
                        onBlur={field.onBlur}
                        className="input-form-text pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => { return setShowPassword(!showPassword); }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </FormControl>
                  {isRegister && <PasswordStrengthMeter password={field.value} />}
                </FormItem>
              );
            }}
          />

          {isRegister && (
            <FormField
              control={form.control}
              name="ageVerification"
              render={({ field }) => {
                return (
                  <FormItem className="w-full flex flex-row items-center justify-center">
                    <FormControl className="mr-4">
                      <Checkbox
                        onCheckedChange={field.onChange}
                        className="bg-sivarbet-primary! scale-150"
                        checked={field.value}
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        onClick={() => {
                          toggleCheckbox(!field.value);
                          field.onChange(!field.value);
                        }}
                      />
                    </FormControl>

                    <FormLabel>{messages.messagesRegister}</FormLabel>
                  </FormItem>
                );
              }}
            />
          )}
          {/* {isRegister && (
            <div className="flex flex-col gap-1 justify-center items-center">

              <FormField
                control={form.control}
                name="turnstileToken"
                render={() => {
                  return (
                    <FormItem>
                      <FormControl>
                        <Turnstile
                          sitekey={apiKey}
                          language="es"
                          onVerify={(token) => {
                            setTurnstileToken(token);
                            handleValidation(token);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          )} */}

          <div className="flex flex-col gap-1">
            <Button
              disabled={disabled || isValidatingEmail}
              type="submit"
              className={`${isRegister ? 'register-button' : ''} marketing-button button-primary-full hover:bg-sivarbet-primary-hover`}
            >
              <div className="relative text-sm leading-[10px] font-medium font-text-sm-medium text-sivarbet-secondary-bg text-center inline-block min-w-[35px]">
                {isValidatingEmail ? 'Validando...' : isRegister ? messages.register : messages.login}
              </div>
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default memo(FormEmailAndPassword);
